import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Stack,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Paper,
    Alert
} from '@mui/material';
import { Close, ModelTraining, Image as ImageIcon, Label } from '@mui/icons-material';

const TrainingConfigDialog = ({ open, onClose, onConfirm, dataset, projectId }) => {
    const [trainingName, setTrainingName] = useState('');
    const [modelType, setModelType] = useState('yolov8n');
    const [framework, setFramework] = useState('ultralytics');
    const [containerName, setContainerName] = useState('cv-training');
    const [epochs, setEpochs] = useState(100);
    const [batchSize, setBatchSize] = useState(16);
    const [imgSize, setImgSize] = useState(640);
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');

    // Model types for different frameworks
    const modelTypes = {
        ultralytics: [
            { value: 'yolov8n', label: 'YOLOv8 Nano (Fast)' },
            { value: 'yolov8s', label: 'YOLOv8 Small' },
            { value: 'yolov8m', label: 'YOLOv8 Medium' },
            { value: 'yolov8l', label: 'YOLOv8 Large' },
            { value: 'yolov8x', label: 'YOLOv8 XLarge (Best accuracy)' },
            { value: 'yolov11n', label: 'YOLOv11 Nano (Latest)' },
            { value: 'yolov11s', label: 'YOLOv11 Small (Latest)' },
            { value: 'yolov11m', label: 'YOLOv11 Medium (Latest)' }
        ],
        detectron2: [
            { value: 'faster_rcnn_R_50_FPN', label: 'Faster R-CNN ResNet50' },
            { value: 'mask_rcnn_R_50_FPN', label: 'Mask R-CNN ResNet50' }
        ],
        mmdetection: [
            { value: 'yolox_s', label: 'YOLOX Small' },
            { value: 'detr_r50', label: 'DETR ResNet50' }
        ]
    };

    const handleSubmit = () => {
        // Validation
        if (!trainingName) {
            setError('Vui lòng nhập tên training job');
            return;
        }

        if (!containerName) {
            setError('Vui lòng nhập tên container');
            return;
        }

        // Prepare training job data
        const jobData = {
            name: trainingName,
            project_id: projectId,
            dataset_id: dataset.id,
            container_name: containerName,
            model_type: modelType,
            framework: framework,
            hyperparameters: {
                epochs: epochs,
                batch_size: batchSize,
                img_size: imgSize,
                device: 'cuda', // GPU by default
                workers: 8,
                optimizer: 'SGD',
                lr0: 0.01,
                momentum: 0.937,
                weight_decay: 0.0005
            },
            description: description || null
        };

        onConfirm(jobData);
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '60vh' }
            }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            Cấu hình Training
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Dataset: {dataset?.name}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={3}>
                    {/* Dataset Info Banner */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: 'primary.lighter',
                            border: '1px solid',
                            borderColor: 'primary.light'
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Thông tin Dataset
                        </Typography>
                        <Stack direction="row" spacing={3}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <ImageIcon fontSize="small" color="primary" />
                                <Typography variant="body2">
                                    <strong>{dataset?.total_images || 0}</strong> images
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Label fontSize="small" color="secondary" />
                                <Typography variant="body2">
                                    <strong>{dataset?.total_labels || 0}</strong> labels
                                </Typography>
                            </Stack>
                            <Typography variant="body2">
                                Format: <strong>{dataset?.format?.toUpperCase()}</strong>
                            </Typography>
                            <Typography variant="body2">
                                Classes: <strong>{dataset?.classes?.length || 0}</strong>
                            </Typography>
                        </Stack>
                    </Paper>

                    {/* Basic Info */}
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Thông tin cơ bản
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Tên Training Job"
                                value={trainingName}
                                onChange={(e) => setTrainingName(e.target.value)}
                                placeholder="VD: yolov8-detection-v1"
                                required
                                helperText="Tên định danh cho training job này"
                            />

                            <TextField
                                fullWidth
                                label="Container Name"
                                value={containerName}
                                onChange={(e) => setContainerName(e.target.value)}
                                required
                                helperText="Tên container Docker để chạy training"
                            />

                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Mô tả (Optional)"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Thêm mô tả về training job này..."
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Model Configuration */}
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Cấu hình Model
                        </Typography>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel>Framework</InputLabel>
                                <Select
                                    value={framework}
                                    label="Framework"
                                    onChange={(e) => {
                                        setFramework(e.target.value);
                                        // Reset model type when framework changes
                                        setModelType(modelTypes[e.target.value]?.[0]?.value || '');
                                    }}
                                >
                                    <MenuItem value="ultralytics">Ultralytics (YOLOv8, YOLOv11)</MenuItem>
                                    <MenuItem value="detectron2">Detectron2 (Facebook)</MenuItem>
                                    <MenuItem value="mmdetection">MMDetection (OpenMMLab)</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Model Type</InputLabel>
                                <Select value={modelType} label="Model Type" onChange={(e) => setModelType(e.target.value)}>
                                    {(modelTypes[framework] || []).map((model) => (
                                        <MenuItem key={model.value} value={model.value}>
                                            {model.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Hyperparameters */}
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Hyperparameters
                        </Typography>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Epochs"
                                    value={epochs}
                                    onChange={(e) => setEpochs(parseInt(e.target.value) || 100)}
                                    InputProps={{ inputProps: { min: 1, max: 1000 } }}
                                    helperText="Số lần training qua toàn bộ dataset"
                                />
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Batch Size"
                                    value={batchSize}
                                    onChange={(e) => setBatchSize(parseInt(e.target.value) || 16)}
                                    InputProps={{ inputProps: { min: 1, max: 128 } }}
                                    helperText="Số lượng samples mỗi batch"
                                />
                            </Stack>

                            <TextField
                                fullWidth
                                type="number"
                                label="Image Size"
                                value={imgSize}
                                onChange={(e) => setImgSize(parseInt(e.target.value) || 640)}
                                InputProps={{ inputProps: { min: 320, max: 1280, step: 32 } }}
                                helperText="Kích thước ảnh input (px)"
                            />
                        </Stack>
                    </Box>

                    {/* Training Tips */}
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            💡 Gợi ý cấu hình:
                        </Typography>
                        <Typography variant="body2">
                            • <strong>Nano/Small models:</strong> Nhanh, phù hợp với edge devices
                            <br />• <strong>Medium/Large models:</strong> Cân bằng tốc độ và độ chính xác
                            <br />• <strong>XLarge models:</strong> Độ chính xác cao nhất, yêu cầu GPU mạnh
                            <br />• <strong>Batch size:</strong> Tăng nếu GPU có nhiều VRAM, giảm nếu gặp lỗi OOM
                        </Typography>
                    </Alert>

                    {/* Error Display */}
                    {error && (
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                    Hủy
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    startIcon={<ModelTraining />}
                    disabled={!trainingName || !containerName}
                >
                    Bắt đầu Training
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrainingConfigDialog;
